import { useState } from 'react';
import { enGB } from 'react-day-picker/locale';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { defaultButtonClasses, secondaryButtonClasses } from '@/constants/ui';
import { useCancelBookingDialog } from '../useCancelBookingDialog';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function buildMonthOptions(from: Date, count = 12): { label: string; value: string; date: Date }[] {
  const options = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() + i, 1);
    options.push({
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      date: d,
    });
  }
  return options;
}

export const FromDateScreen = () => {
  const { fromDate, setFromDate, allowedWeekdays, isElevatedUser, isAdmin, isSlot, navigateTo, goBack } =
    useCancelBookingDialog();
  const [selected, setSelected] = useState<Date | undefined>(fromDate ?? undefined);

  // Slot month picker (C6). Admins are exempt — they fall through to the day picker below, which
  // offers the "cancel now (from today)" shortcut; only Practice Managers see the month picker.
  if (isSlot && !isAdmin) {
    const now = new Date();
    const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const monthOptions = buildMonthOptions(firstOfNextMonth, 12);
    const earliestLabel = `${MONTH_NAMES[firstOfNextMonth.getMonth()]} ${firstOfNextMonth.getFullYear()}`;

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const opt = monthOptions.find((o) => o.value === e.target.value);
      if (opt) {
        setSelected(opt.date);
        setFromDate(opt.date);
      }
    };

    const selectedValue = selected
      ? `${selected.getFullYear()}-${selected.getMonth() + 1}`
      : '';

    return (
      <div data-testid="cancel-dialog-from-date" className="w-full flex flex-col gap-4 bg-white rounded-xl my-4 p-6">
        <h2 className="text-2xl font-semibold">Cancel from when?</h2>
        <p className="text-sm text-gray-600">
          Slots can only be cancelled from the start of a calendar month. The earliest is {earliestLabel}.
        </p>
        <div className="flex flex-col gap-2">
          <label htmlFor="cancel-slot-month-select" className="text-sm font-medium text-gray-700">
            Select cancellation month
          </label>
          <select
            id="cancel-slot-month-select"
            data-testid="cancel-slot-month-picker"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-black"
            value={selectedValue}
            onChange={handleMonthChange}
          >
            <option value="" disabled>Choose a month…</option>
            {monthOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2 mt-2">
          <Button
            data-testid="cancel-from-date-continue"
            className={`w-full ${defaultButtonClasses}`}
            disabled={!selected}
            onClick={() => navigateTo('confirm')}
          >
            Continue
          </Button>
          <Button variant="outline" className={`w-full ${secondaryButtonClasses}`} onClick={goBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  // Non-slot recurring: existing weekday day-picker
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 52 * 7);

  const disabled = (date: Date) => {
    if (date < today) return true;
    if (date > maxDate) return true;
    return !allowedWeekdays.includes(date.getDay());
  };

  const choose = (d: Date) => {
    setSelected(d);
    setFromDate(d);
  };

  return (
    <div data-testid="cancel-dialog-from-date" className="w-full flex flex-col gap-4 bg-white rounded-xl my-4 p-6">
      <h2 className="text-2xl font-semibold">Cancel from when?</h2>
      <p className="text-sm text-gray-600">
        Pick the first date to cancel. That occurrence and every later one will be cancelled; earlier ones are kept.
      </p>
      {isElevatedUser && (
        <Button
          data-testid="cancel-from-today"
          variant="outline"
          className={`w-full ${secondaryButtonClasses}`}
          onClick={() => {
            const t = new Date();
            t.setHours(0, 0, 0, 0);
            choose(t);
            navigateTo('confirm');
          }}
        >
          Cancel the whole series now (from today)
        </Button>
      )}
      <div data-testid="cancel-dialog-day-calendar" className="flex justify-center">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? today}
          captionLayout="dropdown"
          // Bound the dropdown's month/year range to the valid cancellation window (today →
          // today+52wk). Without these, react-day-picker's year dropdown defaults to a ~100-year
          // BACKWARD range (1926→current), letting you scroll into the past. Mirrors DateTumbler.
          startMonth={today}
          endMonth={maxDate}
          disabled={disabled}
          onSelect={(d) => d && choose(d)}
          locale={enGB}
          // Hide the prev/next month arrows — the month/year dropdown caption already handles
          // navigation, so the arrows were redundant (and, with the shared Calendar's absolute
          // nav positioning, just re-opened the dropdown and looked odd).
          hideNavigation
          classNames={{
            month_grid: "min-w-[230px]",
          }}
        />
      </div>
      <div className="flex flex-col gap-2 mt-2">
        <Button
          data-testid="cancel-from-date-continue"
          className={`w-full ${defaultButtonClasses}`}
          disabled={!selected}
          onClick={() => navigateTo('confirm')}
        >
          Continue
        </Button>
        <Button variant="outline" className={`w-full ${secondaryButtonClasses}`} onClick={goBack}>
          Back
        </Button>
      </div>
    </div>
  );
};
